import { WORLD_SIZE } from "../constants.js";

const TYPE_COLOR = {
    plateau: "#e7a15a",
    canyon: "#ffb089",
    valley: "#d9a27a",
    plains: "#f3ddc4",
    dunes: "#f0d7a4",
    crater: "#ffc2a1",
    mountains: "#ff8d5c",
};

export default class MapView {
    constructor({ onSelect, onEdit, onCommit }) {
        this.onSelect = onSelect;
        this.onEdit = onEdit;
        this.onCommit = onCommit;

        this.layout = null;
        this.heights = null;
        this.resolution = WORLD_SIZE;
        this.layoutSize = WORLD_SIZE;
        this.centerX = WORLD_SIZE / 2;
        this.centerZ = WORLD_SIZE / 2;
        this.span = WORLD_SIZE;
        this.selectedId = null;
        this.drag = null;
        this.markers = null;
        this.open = false;

        this.root = document.createElement("div");
        this.root.id = "terrain-map";
        this.root.innerHTML = `
            <div class="terrain-map-bar">地形图 · 拖动摆放 · 角点缩放 · 圆点旋转 · M 关闭</div>
            <canvas></canvas>
            <div class="terrain-map-status"></div>
        `;
        document.body.appendChild(this.root);
        this.canvas = this.root.querySelector("canvas");
        this.ctx = this.canvas.getContext("2d");
        this.status = this.root.querySelector(".terrain-map-status");
        this.base = document.createElement("canvas");
        this.baseCtx = this.base.getContext("2d");

        this.onPointerDown = (event) => this.pointerDown(event);
        this.onPointerMove = (event) => this.pointerMove(event);
        this.onPointerUp = (event) => this.pointerUp(event);
        this.onWheel = (event) => this.wheel(event);
        this.onKeyDown = (event) => this.keyDown(event);
        this.onContext = (event) => event.preventDefault();

        this.canvas.addEventListener("pointerdown", this.onPointerDown);
        window.addEventListener("pointermove", this.onPointerMove);
        window.addEventListener("pointerup", this.onPointerUp);
        this.canvas.addEventListener("wheel", this.onWheel, { passive: false });
        this.canvas.addEventListener("contextmenu", this.onContext);
        window.addEventListener("keydown", this.onKeyDown);
    }

    setStatus(text) {
        this.status.textContent = text;
    }

    setLayout(layout) {
        this.layout = layout;
        if (this.open) this.paint();
    }

    setSelected(id) {
        this.selectedId = id;
        if (this.open) this.paint();
    }

    setMarkers(markers) {
        this.markers = markers;
    }

    setHeights(heights, resolution, layoutSize) {
        this.heights = heights;
        this.resolution = resolution;
        this.layoutSize = layoutSize;
        this.redrawBase();
        if (this.open) this.paint();
    }

    toggle() {
        this.open = !this.open;
        this.root.classList.toggle("open", this.open);
        if (this.open) {
            this.resize();
            this.paint();
        }
    }

    resize() {
        const rect = this.canvas.getBoundingClientRect();
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const width = Math.max(1, Math.floor(rect.width * dpr));
        const height = Math.max(1, Math.floor(rect.height * dpr));
        if (this.canvas.width !== width || this.canvas.height !== height) {
            this.canvas.width = width;
            this.canvas.height = height;
        }
    }

    redrawBase() {
        if (!this.heights) return;
        const size = 512;
        this.base.width = size;
        this.base.height = size;
        const image = this.baseCtx.createImageData(size, size);
        const data = image.data;
        const resolution = this.resolution;
        const layoutSize = this.layoutSize;
        const heights = this.heights;

        for (let y = 0; y < size; y++) {
            const row = Math.min(resolution - 1, Math.floor((y / size) * resolution));
            const rowNext = Math.min(resolution - 1, row + 1);
            for (let x = 0; x < size; x++) {
                const col = Math.min(resolution - 1, Math.floor((x / size) * resolution));
                const colNext = Math.min(resolution - 1, col + 1);
                const height = heights[row * resolution + col];
                const hx = heights[row * resolution + colNext];
                const hz = heights[rowNext * resolution + col];
                const shade = hillshade(height, hx, hz);
                const t = Math.max(0, Math.min(1, (height - 8) / 70));
                const index = (y * size + x) * 4;
                data[index] = (92 + t * 150) * shade;
                data[index + 1] = (32 + t * 70) * shade;
                data[index + 2] = (16 + t * 24) * shade;
                data[index + 3] = 255;
            }
        }
        this.baseCtx.putImageData(image, 0, 0);
        this.layoutSize = layoutSize;
    }

    paint() {
        if (!this.open) return;
        this.resize();
        const ctx = this.ctx;
        const width = this.canvas.width;
        const height = this.canvas.height;
        ctx.clearRect(0, 0, width, height);

        if (this.base.width > 0) {
            const half = this.span / 2;
            const x0 = (this.centerX - half) / this.layoutSize;
            const z0 = (this.centerZ - half) / this.layoutSize;
            const w = this.span / this.layoutSize;
            ctx.drawImage(
                this.base,
                x0 * this.base.width,
                z0 * this.base.height,
                w * this.base.width,
                w * this.base.height,
                0,
                0,
                width,
                height,
            );
        }

        if (!this.layout) return;
        for (let i = 0; i < this.layout.features.length; i++) {
            this.drawFeature(this.layout.features[i], this.layout.features[i].id === this.selectedId);
        }
        this.drawMarkers();
    }

    drawFeature(feature, selected) {
        const ctx = this.ctx;
        const corners = [
            localToWorld(feature, -1, -1),
            localToWorld(feature, 1, -1),
            localToWorld(feature, 1, 1),
            localToWorld(feature, -1, 1),
        ].map((point) => this.columnToCanvas(point.x, point.z));

        ctx.beginPath();
        ctx.moveTo(corners[0].x, corners[0].y);
        for (let i = 1; i < corners.length; i++) ctx.lineTo(corners[i].x, corners[i].y);
        ctx.closePath();
        const color = selected ? "#fff6ea" : TYPE_COLOR[feature.type] || "#f3ddc4";
        ctx.strokeStyle = color;
        ctx.lineWidth = selected ? 2.5 : 1.5;
        ctx.stroke();

        const center = this.columnToCanvas(feature.x, feature.z);
        if (selected) {
            const scaleHandle = this.columnToCanvas(
                localToWorld(feature, 1, 1).x,
                localToWorld(feature, 1, 1).z,
            );
            const rotateHandle = this.columnToCanvas(
                localToWorld(feature, 1.35, 0).x,
                localToWorld(feature, 1.35, 0).z,
            );
            ctx.strokeStyle = "#fff6ea";
            ctx.beginPath();
            ctx.moveTo(center.x, center.y);
            ctx.lineTo(rotateHandle.x, rotateHandle.y);
            ctx.stroke();
            ctx.fillStyle = "#fff6ea";
            ctx.fillRect(scaleHandle.x - 5, scaleHandle.y - 5, 10, 10);
            ctx.beginPath();
            ctx.arc(rotateHandle.x, rotateHandle.y, 6, 0, Math.PI * 2);
            ctx.fill();
        }

        this.drawLabel(feature.type, center.x, center.y, color);
    }

    drawLabel(text, x, y, color) {
        const ctx = this.ctx;
        const cssWidth = this.canvas.getBoundingClientRect().width || this.canvas.width;
        const dpr = this.canvas.width / cssWidth;
        const fontSize = 11 * dpr;
        ctx.save();
        ctx.font = `600 ${fontSize}px "PingFang SC", "Segoe UI", system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.lineJoin = "round";
        ctx.lineWidth = 3 * dpr;
        ctx.strokeStyle = "rgba(12, 5, 2, 0.88)";
        ctx.strokeText(text, x, y);
        ctx.fillStyle = color;
        ctx.fillText(text, x, y);
        ctx.restore();
    }

    drawMarkers() {
        if (!this.markers || !this.layout) return;
        const ctx = this.ctx;
        const spawn = this.columnToCanvas(this.layout.spawn.x, this.layout.spawn.z);
        ctx.strokeStyle = "#7dfbe4";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(spawn.x - 6, spawn.y);
        ctx.lineTo(spawn.x + 6, spawn.y);
        ctx.moveTo(spawn.x, spawn.y - 6);
        ctx.lineTo(spawn.x, spawn.y + 6);
        ctx.stroke();

        const robot = this.columnToCanvas(this.markers.robotI, this.markers.robotJ);
        ctx.fillStyle = "#8ec8ff";
        ctx.beginPath();
        ctx.arc(robot.x, robot.y, 4, 0, Math.PI * 2);
        ctx.fill();

        const cam = this.columnToCanvas(this.markers.camX, this.markers.camZ);
        const ahead = this.columnToCanvas(
            this.markers.camX + this.markers.dirX * 40,
            this.markers.camZ + this.markers.dirZ * 40,
        );
        ctx.strokeStyle = "rgba(255,255,255,0.7)";
        ctx.beginPath();
        ctx.moveTo(cam.x, cam.y);
        ctx.lineTo(ahead.x, ahead.y);
        ctx.stroke();
    }

    columnToCanvas(x, z) {
        const half = this.span / 2;
        const u = (x - (this.centerX - half)) / this.span;
        const v = (z - (this.centerZ - half)) / this.span;
        return { x: u * this.canvas.width, y: v * this.canvas.height };
    }

    canvasToColumn(x, y) {
        const half = this.span / 2;
        return {
            x: this.centerX - half + (x / this.canvas.width) * this.span,
            z: this.centerZ - half + (y / this.canvas.height) * this.span,
        };
    }

    eventToCanvas(event) {
        const rect = this.canvas.getBoundingClientRect();
        return {
            x: ((event.clientX - rect.left) / rect.width) * this.canvas.width,
            y: ((event.clientY - rect.top) / rect.height) * this.canvas.height,
        };
    }

    pointerDown(event) {
        if (!this.open || !this.layout) return;
        event.preventDefault();
        event.stopPropagation();
        this.canvas.setPointerCapture?.(event.pointerId);
        const point = this.eventToCanvas(event);
        if (event.button === 1 || event.button === 2 || event.altKey) {
            this.drag = { mode: "pan", x: point.x, y: point.y, button: event.button };
            return;
        }

        const hit = this.hitTest(point);
        if (!hit) {
            this.selectedId = null;
            this.onSelect(null);
            this.drag = { mode: "pan", x: point.x, y: point.y, button: 0 };
            this.paint();
            return;
        }

        this.selectedId = hit.feature.id;
        this.onSelect(hit.feature);
        const raw = toLocalRaw(hit.feature, this.canvasToColumn(point.x, point.y));
        this.drag = {
            mode: hit.mode,
            id: hit.feature.id,
            x: point.x,
            y: point.y,
            originX: hit.feature.x,
            originZ: hit.feature.z,
            rotation: hit.feature.rotation,
            angle: Math.atan2(raw.across, raw.along),
        };
        this.paint();
    }

    pointerMove(event) {
        if (!this.drag || !this.layout) return;
        const point = this.eventToCanvas(event);
        const feature = this.layout.features.find((item) => item.id === this.drag.id);

        if (this.drag.mode === "pan") {
            const dx = ((point.x - this.drag.x) / this.canvas.width) * this.span;
            const dz = ((point.y - this.drag.y) / this.canvas.height) * this.span;
            this.centerX -= dx;
            this.centerZ -= dz;
            this.drag.x = point.x;
            this.drag.y = point.y;
            this.paint();
            return;
        }
        if (!feature) return;

        const column = this.canvasToColumn(point.x, point.y);
        if (this.drag.mode === "move") {
            const start = this.canvasToColumn(this.drag.x, this.drag.y);
            feature.x = clamp(this.drag.originX + column.x - start.x, 0, this.layoutSize);
            feature.z = clamp(this.drag.originZ + column.z - start.z, 0, this.layoutSize);
        } else if (this.drag.mode === "scale") {
            const raw = toLocalRaw(feature, column);
            let sx = Math.max(8, Math.abs(raw.along));
            let sz = Math.max(8, Math.abs(raw.across));
            if (event.shiftKey) {
                const uniform = Math.max(sx, sz);
                sx = uniform;
                sz = uniform;
            }
            feature.sx = sx;
            feature.sz = sz;
        } else if (this.drag.mode === "rotate") {
            const raw = toLocalRaw(feature, column);
            const angle = Math.atan2(raw.across, raw.along);
            feature.rotation = this.drag.rotation + angle - this.drag.angle;
        }

        this.onEdit();
        this.paint();
    }

    pointerUp(event) {
        if (!this.drag) return;
        const edited = this.drag.mode !== "pan";
        this.drag = null;
        if (edited) this.onCommit();
        if (event.pointerId !== undefined) {
            this.canvas.releasePointerCapture?.(event.pointerId);
        }
    }

    wheel(event) {
        if (!this.open) return;
        event.preventDefault();
        const point = this.eventToCanvas(event);
        const before = this.canvasToColumn(point.x, point.y);
        const next = this.span * (event.deltaY > 0 ? 1.08 : 0.92);
        this.span = clamp(next, 48, this.layoutSize);
        const after = this.canvasToColumn(point.x, point.y);
        this.centerX += before.x - after.x;
        this.centerZ += before.z - after.z;
        this.paint();
    }

    keyDown(event) {
        if (isTyping(event)) return;
        if (event.code === "KeyM" && !event.repeat) {
            event.preventDefault();
            this.toggle();
        }
        if (!this.open || event.code !== "Delete" && event.code !== "Backspace") return;
        if (!this.selectedId || isTyping(event)) return;
        event.preventDefault();
        const index = this.layout.features.findIndex((feature) => feature.id === this.selectedId);
        if (index < 0) return;
        this.layout.features.splice(index, 1);
        this.selectedId = null;
        this.onSelect(null);
        this.onCommit();
        this.paint();
    }

    hitTest(point) {
        const features = this.layout.features;
        for (let i = features.length - 1; i >= 0; i--) {
            const feature = features[i];
            if (feature.id === this.selectedId) {
                const scale = this.columnToCanvas(
                    localToWorld(feature, 1, 1).x,
                    localToWorld(feature, 1, 1).z,
                );
                const rotate = this.columnToCanvas(
                    localToWorld(feature, 1.35, 0).x,
                    localToWorld(feature, 1.35, 0).z,
                );
                if (distance(point, rotate) < 12) return { feature, mode: "rotate" };
                if (distance(point, scale) < 12) return { feature, mode: "scale" };
            }
            const column = this.canvasToColumn(point.x, point.y);
            const local = toLocal(feature, column);
            if (Math.abs(local.u) <= 1 && Math.abs(local.v) <= 1) {
                return { feature, mode: "move" };
            }
        }
        return null;
    }
}

function hillshade(height, east, south) {
    const nx = height - east;
    const nz = height - south;
    const ny = 2.4;
    const length = Math.hypot(nx, ny, nz) || 1;
    const light = Math.max(0, (nx * -0.45 + ny * 0.85 + nz * -0.25) / length);
    return 0.42 + light * 0.7;
}

function localToWorld(feature, u, v) {
    const cos = Math.cos(feature.rotation);
    const sin = Math.sin(feature.rotation);
    return {
        x: feature.x + u * feature.sx * cos - v * feature.sz * sin,
        z: feature.z + u * feature.sx * sin + v * feature.sz * cos,
    };
}

function toLocalRaw(feature, column) {
    const dx = column.x - feature.x;
    const dz = column.z - feature.z;
    const cos = Math.cos(feature.rotation);
    const sin = Math.sin(feature.rotation);
    return {
        along: dx * cos + dz * sin,
        across: -dx * sin + dz * cos,
    };
}

function toLocal(feature, column) {
    const raw = toLocalRaw(feature, column);
    return { u: raw.along / feature.sx, v: raw.across / feature.sz };
}

function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
}

function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

function isTyping(event) {
    const tag = event.target?.tagName;
    return tag === "INPUT" || tag === "TEXTAREA";
}
